import React, { useState, useContext, createContext, useRef } from 'react';
import PropTypes from 'prop-types';
import chatApiService from '../services/chatApiService';
import { toast } from 'react-toastify';

export const ChatContext = createContext();

const ChatProvider = ({ children, provider = 'openai', model = 'gpt-4o-mini' }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [textAreaInitialHeight, setTextAreaInitialHeight] = useState(32);
  const [scrollToBottom, setScrollToBottom] = useState(true);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const messagesEndRef = useRef();

  // Formatear fecha para los mensajes
  const formatDate = (date) => {
    const options = {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    };

    const now = date
      .toLocaleString('es-ES', options)
      .split(',')
      .map(item => item.trim());

    return {
      day: now[0],
      hour: now[2] || now[1],
      date: now[1] || now[0]
    };
  };

  // Agregar mensaje al chat
  const addMessage = (message, sender = 'user') => {
    const newMessage = {
      id: Date.now() + Math.random(),
      message: message.replace(/(?:\r\n|\r|\n)/g, '<br>'),
      sender,
      time: formatDate(new Date()),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);
    setScrollToBottom(true);
    return newMessage;
  };

  // Enviar mensaje a ChatGPT
  const sendMessage = async (userMessage) => {
    if (!userMessage.trim()) return;

    try {
      setIsLoading(true);
      
      // Agregar mensaje del usuario
      addMessage(userMessage, 'user');

      if (process.env.NODE_ENV === 'development') {
        console.log('📤 Enviando mensaje al chat API:', userMessage);
      }

      // Enviar al servicio de IA con proveedor y modelo seleccionados
      const response = await chatApiService.sendMessage(userMessage, { provider, model });

      if (process.env.NODE_ENV === 'development') {
        console.log('📥 Respuesta del chat API:', response);
        console.log('📊 Response analysis:', {
          type: typeof response,
          isObject: typeof response === 'object',
          hasSuccess: response?.hasOwnProperty?.('success'),
          successValue: response?.success,
          hasMessage: response?.hasOwnProperty?.('message'),
          messageLength: response?.message?.length
        });
      }

      if (response && response.success) {
        // Agregar respuesta del asistente con información adicional
        const assistantMessage = response.message;
        const debugInfo = response.isTestMode ? ` [Modo: ${response.provider}/${response.model}]` : '';
        
        addMessage(assistantMessage + debugInfo, 'assistant');
        
        // Mostrar notificación si está en modo de prueba
        if (response.isTestMode) {
          toast.info(`Respuesta en modo ${response.provider}`, { autoClose: 3000 });
        }
      } else if (response && response.error) {
        // Error devuelto por el servicio
        console.warn('⚠️ Error del servicio de chat:', response.message);
        addMessage(response.message || 'Lo siento, ha ocurrido un error al procesar tu mensaje.', 'assistant');
        toast.error('Error al procesar mensaje');
      } else {
        // Respuesta inesperada
        console.error('❌ Respuesta inesperada del servicio:', response);
        addMessage('Recibí una respuesta inesperada del servidor. Por favor intenta nuevamente.', 'assistant');
        toast.error('Respuesta inesperada del servidor');
      }
    } catch (error) {
      console.error('❌ Error crítico enviando mensaje:', error);
      
      // Mensaje de error más específico
      let errorMessage = 'Error de conexión. Por favor verifica tu conexión e intenta nuevamente.';
      if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      addMessage(errorMessage, 'assistant');
      toast.error('Error de conexión con el servicio de chat');
    } finally {
      setIsLoading(false);
    }
  };

  // Generar análisis automático
  const generateInsight = async (dataType, data) => {
    try {
      setIsLoading(true);
      const response = await chatApiService.generateInsight(dataType, data);
      
      if (response.success) {
        addMessage(`Análisis generado automáticamente:`, 'system');
        addMessage(response.message, 'assistant');
        setIsOpen(true); // Abrir chat para mostrar el insight
      }
    } catch (error) {
      console.error('Error generating insight:', error);
      toast.error('Error al generar análisis automático');
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar preguntas sugeridas
  const loadSuggestedQuestions = async () => {
    try {
      const questions = await chatApiService.suggestQuestions();
      setSuggestedQuestions(questions);
    } catch (error) {
      console.error('Error loading suggested questions:', error);
    }
  };

  // Limpiar conversación
  const clearChat = () => {
    setMessages([]);
    chatApiService.clearHistory();
    toast.success('Conversación limpiada');
  };

  // Alternar chat (abrir/cerrar)
  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen && suggestedQuestions.length === 0) {
      loadSuggestedQuestions();
    }
  };

  // Scroll automático hacia abajo
  const scrollToBottomHandler = () => {
    if (scrollToBottom && messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      setScrollToBottom(false);
    }
  };

  // Obtener resumen de la conversación
  const getConversationSummary = () => {
    const userMessages = messages.filter(msg => msg.sender === 'user').length;
    const assistantMessages = messages.filter(msg => msg.sender === 'assistant').length;
    
    return {
      totalMessages: messages.length,
      userMessages,
      assistantMessages,
      lastActivity: messages.length > 0 ? messages[messages.length - 1].timestamp : null
    };
  };

  const value = {
    // Estado
    messages,
    isLoading,
    isOpen,
    textAreaInitialHeight,
    scrollToBottom,
    suggestedQuestions,

    // Funciones principales
    sendMessage,
    addMessage,
    generateInsight,
    clearChat,
    toggleChat,
    loadSuggestedQuestions,

    // Configuración
    setTextAreaInitialHeight,
    setScrollToBottom,
    scrollToBottomHandler,

    // Información
    getConversationSummary,

    // Refs
    messagesEndRef
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

ChatProvider.propTypes = { 
  children: PropTypes.node.isRequired 
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

export default ChatProvider;
